import { useAuth0 } from '@auth0/auth0-react';
import { IonIcon } from '@ionic/react';
import { Button, TextField } from '@material-ui/core';
import Dialog from '@reach/dialog';
import { closeCircleOutline, linkOutline } from 'ionicons/icons';
import { useSnackbar } from 'notistack';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import apiService from '../../../services/api/api.serivce';
import styles from './Connect.module.scss';

interface Props {
  data: any;
}

const useDeleteCall = (id: string) => {
  const [isLoading, setLoading] = useState(false);
  const [result, setResult] = useState(false);
  const [error, setError] = useState(false);

  const { getAccessTokenSilently } = useAuth0();

  const execute = async (connectToId: string) => {
    setResult(false);
    setError(false);
    setLoading(true);

    const accessToken = await getAccessTokenSilently();

    apiService
      .connectGroup([id, connectToId], accessToken)
      .then(() => {
        setLoading(false);
        setResult(true);
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  };

  return { isLoading, execute, result, error };
};

const Connect: React.FC<Props> = ({ data }) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const [connectUrl, setConnectUrl] = React.useState('');

  const updateConnectUrl = (event: ChangeEvent<HTMLInputElement>) => {
    setConnectUrl(event.target.value);
  };

  const [showDialog, setShowDialog] = React.useState(false);
  const { isLoading, execute, result, error } = useDeleteCall(data.items[0].id);

  const open = () => setShowDialog(true);
  const close = useCallback(() => {
    if (!isLoading) setShowDialog(false);
  }, [isLoading, setShowDialog]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar('Could not connect. Please try again', {
        variant: 'warning',
      });
    }
  }, [error, enqueueSnackbar]);

  useEffect(() => {
    if (result) {
      enqueueSnackbar(
        'New connection was made. It might take a bit of time for it to show up in the search. Reload to see it here.',
        {
          variant: 'success',
        }
      );
      close();
    }
  }, [result, enqueueSnackbar, close, history]);

  const handleSave = () => {
    const match = connectUrl.match(/\/([0-9]*_[0-9A-Za-z]*)/);
    if (match && match.length) {
      execute(match[1]);
    }
  };
  return (
    <>
      <Button
        type="button"
        variant="outlined"
        onClick={open}
        startIcon={<IonIcon icon={linkOutline} />}>
        Connect to other provience
      </Button>

      <Dialog isOpen={showDialog} onDismiss={close}>
        <div className={styles.ModalHeader}>
          <h1 className={styles.ModalTitle}>Connect to other provience</h1>
          <button className={styles.CloseButton} onClick={close}>
            <IonIcon icon={closeCircleOutline} ariaLabel="Close Modal" />
          </button>
        </div>
        <div className={styles.ModalContent}>
          Paste the url of the provience to want to connect to here:
          <br />
          <div className={styles.UrlInputWrapper}>
            <TextField
              className={styles.input}
              label="Url"
              type="text"
              placeholder={`${window.location.origin}/details/542_23`}
              value={connectUrl}
              onChange={updateConnectUrl}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </div>
        </div>
        <div className={styles.ModalActions}>
          <Button type="button" variant="outlined" onClick={close}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isLoading || connectUrl === ''}
            variant="contained"
            color="primary"
            onClick={handleSave}>
            {isLoading ? 'Loading ...' : 'Connect'}
          </Button>
        </div>
      </Dialog>
    </>
  );
};

export default Connect;
